import { observable, action, makeObservable, runInAction } from "mobx";
import { SidebarItemProps } from "./Sidebar";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { subMenus, SubMenusObjectConfig } from "./subMenus";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { API } from "General/API";
import { InfoBoxSchema, mapSidebarMenus } from "Stores/helpers";

export interface SubmenuItem {
  label: string | JSX.Element;
  link: string;
  icon?: IconName;
}
export interface MenuItems extends Omit<API_ModuleMenuInfo, "module" | "label">, InfoBoxSchema {
  children: API_ModuleMenuInfo[];
  onClick?: () => void;
}

export interface ParsedMenuItems {
  projectMenu: MenuItems[];
  mainMenu: MenuItems[];
  secondaryMenu: MenuItems[];
}

// Store is to handle opening of submenus by computing an array of selected items by pathname.
// TODO: Consider to replace antd Menu with custom implementation without mobx stores and stuff.
class SidebarStore {
  menuItems: Array<SidebarItemProps> = [];
  secondaryMenuItems: Array<SidebarItemProps> = [];
  isCollapsed: boolean = true;
  subMenus: SubMenusObjectConfig = {};
  activeLink: string = "";
  activePath: string = "";
  menu: MenuItems[] = [];

  constructor() {
    makeObservable(this, {
      menuItems: observable,
      menu: observable,
      secondaryMenuItems: observable,
      activeLink: observable,
      activePath: observable,
      isCollapsed: observable,
      subMenus: observable,
      toggleSidebar: action.bound,
      setSidebarCollapse: action.bound,
      setMenuItems: action.bound,
      setMenu: action.bound,
      setSecondaryMenuItems: action.bound,
      pushSubMenu: action.bound,
      deleteSubMenu: action.bound,
      setSubMenusForCurrentProject: action.bound,
      setActiveRoutes: action.bound,
      clear: action.bound,
      fetch: action.bound,
    });
  }

  url() {
    return API_URL.PROJECTS.getMenuInfo(projectsDataStore.projectId);
  }

  clear() {
    this.activeLink = "";
    this.activePath = "";
    this.menu = [];
  }

  @handleStoreState()
  async fetch() {
    try {
      const response = await API.getData<API_MenuInfo>(this.url());
      runInAction(() => {
        this.setMenu(mapSidebarMenus(response.modules as MenuItems[], response.prName, projectsDataStore.projectId));
      });
    } catch (error) {
      throw error;
    }
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
  }

  setSidebarCollapse(value: boolean) {
    this.isCollapsed = value;
  }

  setMenuItems(items: Array<SidebarItemProps>) {
    this.menuItems = items;
  }

  setMenu(items: MenuItems[]) {
    this.menu = items;
  }

  setSecondaryMenuItems(items: Array<SidebarItemProps>) {
    this.secondaryMenuItems = items;
  }

  setActiveRoutes(link: string, path: string) {
    if (link) {
      this.activeLink = link;
    }
    if (path) {
      this.activePath = path;
    }
  }

  pushSubMenu(module: PROJECT_MODULES) {
    this.subMenus = {
      ...this.subMenus,
      [module]: {
        config: [...this.subMenus[module].config, { label: "Label", link: "link" }],
      },
    };
  }

  deleteSubMenu(module: PROJECT_MODULES) {
    this.subMenus = {
      ...this.subMenus,
      [module]: {
        config: this.subMenus[module].config.filter((v) => v.label !== "Label"),
      },
    };
  }

  async setSubMenusForCurrentProject() {
    this.subMenus = subMenus;
  }
}

export const sidebarStore = new SidebarStore();
